/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { HelpCircle } from 'lucide-react';
import { ComponentPropsWithoutRef, ElementRef, ReactNode, forwardRef } from 'react';

import { Tooltip, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

import { AssertionInput, AssertionOperator } from '@/commons/data';
import { cn } from '@/lib/utils';

const getToolTipText = (operator: AssertionInput | AssertionOperator) => {
    switch (operator) {
        case 'contains':
            return 'Returns PASS if the selected input contains a string';
        case 'notContains':
            return 'Returns PASS if the selected input does Not contain a string';
        case 'notEquals':
            return 'Returns PASS if the selected input strictly is different from a string';
        case 'equals':
            return 'Returns PASS if the selected input strictly is strictly equal to a string';
        case 'matches':
            return 'Returns PASS if the selected input matches a regex';
        case 'notMatches':
            return 'Returns PASS if not match is found for a regex in the selected input';
        case 'pageText':
            return 'Evaluate the assertion on all the content of the current page';
        case 'url':
            return 'Evaluate the assertion on the current page url';
        case 'title':
            return 'Evaluate the assertion on the current page title';
    }
};

type TooltipOverParentProps = {
    item?: AssertionInput | AssertionOperator;
    renderTooltip?: () => ReactNode;
    renderTrigger?: () => ReactNode;
    iconSize?: number;
    tooltipPadding?: string;
    delayDuration?: number;
};

// Redefining TooltipContent so that it's not clipped when inside a select
// https://github.com/shadcn-ui/ui/issues/129
const TooltipContent = forwardRef<
    ElementRef<typeof TooltipPrimitive.Content>,
    ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & { tooltipPadding?: string }
>(({ className, tooltipPadding = 'px-3 py-1.5', sideOffset = 4, ...props }, ref) => (
    <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                `z-50 overflow-hidden rounded-md border border-slate-100
                 bg-white text-sm text-slate-700 shadow-md animate-in
                 fade-in-50 data-[side=bottom]:slide-in-from-top-1
                 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1
                 data-[side=right]:slide-in-from-left-1 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-400`,
                tooltipPadding,
                className,
            )}
            {...props}
        />
    </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TooltipOverParent = ({
    item,
    renderTooltip,
    renderTrigger,
    iconSize,
    tooltipPadding,
    delayDuration,
}: TooltipOverParentProps) => {
    return (
        <TooltipProvider delayDuration={delayDuration ?? 700}>
            <Tooltip>
                <TooltipTrigger asChild>
                    {renderTrigger ? renderTrigger() : <HelpCircle size={iconSize ?? 10} color="black" />}
                </TooltipTrigger>
                <TooltipContent tooltipPadding={tooltipPadding}>
                    {item ? getToolTipText(item) : renderTooltip ? renderTooltip() : ''}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default TooltipOverParent;
